/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { Label } from '@msdyn365-commerce-modules/order-management';
import {
    getSalesOrderHistoryWithHydrationsAction,
    GetSalesOrderHistoryWithHydrationsInput,
    IOrderHistory,
    IPaging
} from './actions/get-order-shipment-history';
import { getSalesOrder, ISalesOrder } from './components/get-sales-order';
import { OrderHistoryFilter, OrderHistoryFilterState } from './components/order-history-filter';
import { OrderHistoryTableComponent } from './components/table-view';
import { IOrderHistorySearchData } from './order-history-search.data';
import { IOrderHistorySearchProps } from './order-history-search.props.autogenerated';
import DatePickerFilter from './components/date-picker-filter';

/**
 * Specifies different layouts for order history.
 */
export enum OrderHistoryViewMode {
    listView = 0,
    detailedView = 1
}

export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
    orderCountLabel?: React.ReactNode;
    extraActions?: React.ReactNode;
}

export interface IList {
    listProps: INodeProps;
    salesOrders: ISalesOrder[];
}

export interface IOrderHistoryViewState {
    isDataReady: boolean;
    hasError: boolean;
    isLoading: boolean;
    isShowMoreButton: boolean;
    invalidStartDate: boolean;
    invalidEndDate: boolean;
}

export interface IOrderHistoryViewProps extends IOrderHistorySearchProps<IOrderHistorySearchData> {
    orderHistory: IOrderHistory;
    orderHistoryProps: IModuleProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    header: IHeader;
    backToShoppingLink?: React.ReactNode;
    list?: IList;
    table?: React.ReactNode;
    moreButton?: React.ReactNode;
    viewState: IOrderHistoryViewState;
    orderHistorySearchFieldPlaceholder: React.ReactNode;
    getHistory: (searchFilter: string) => void;
    backOrderOnlyCheckbox: boolean;
    backOrderOnlyToggle: React.ReactNode;
    datePickers: React.ReactNode;
    showDatePickers: boolean;
    searchByPoCheckbox: boolean;
    searchByPoToggle: React.ReactNode;
    searchPlaceholderText: string;
}

interface IOrderHistoryState {
    isBackOrderOnlyChecked: boolean;
}
interface IOrderHistoryPoState {
    isSearchByPoChecked: boolean;
}

export interface IOrderHistoryDatePickerFilterState {
    startDate: Date | null;
    endDate: Date | null;
}

/**
 *
 * OrderHistory component.
 * @extends {React.Component<IOrderHistoryProps<IOrderHistorySearchData>>}
 */
@observer
class OrderHistory extends React.Component<IOrderHistorySearchProps<IOrderHistorySearchData>> {
    @observable private orderHistory!: IOrderHistory;

    @observable private viewState!: IOrderHistoryViewState;

    @observable private _viewMode: OrderHistoryViewMode;

    @observable private datePickerState: IOrderHistoryDatePickerFilterState;

    private filterState: OrderHistoryFilterState;

    @observable private backOrderToggleState: IOrderHistoryState;

    @observable private searchByPoToggleState: IOrderHistoryPoState;

    private pageSize!: number;

    private paging!: IPaging;

    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    constructor(props: IOrderHistorySearchProps<IOrderHistorySearchData>) {
        super(props);
        this.initState();
        this._viewMode = OrderHistoryViewMode.detailedView;
        this.backOrderToggleState = {
            isBackOrderOnlyChecked: false
        };
        this.searchByPoToggleState = {
            isSearchByPoChecked: false
        };
        this.datePickerState = {
            startDate: null,
            endDate: null
        };
        this.filterState = OrderHistoryFilterState.CurrentUser;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
    }

    public componentDidMount(): void {
        if (this.props.context.actionContext.requestContext.user.isAuthenticated) {
            this._updateOrderHistory();
        } else {
            this.viewState.isDataReady = true;
        }
    }

    public shouldComponentUpdate(nextProps: IOrderHistorySearchProps<IOrderHistorySearchData>, nextState: IOrderHistoryViewState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    private _handleDateChange = (dateToChange: string, newDate: Date) => {
        const start = this.datePickerState.startDate;
        const end = this.datePickerState.endDate;
        this.viewState.invalidStartDate = false;
        this.viewState.invalidEndDate = false;

        if (dateToChange === 'startDate' && end && newDate.getTime() > end.getTime()) {
            this.viewState.invalidStartDate = true;
            return;
        }
        if (dateToChange === 'endDate' && start && newDate.getTime() < start.getTime()) {
            this.viewState.invalidEndDate = true;
            return;
        } else {
            this.datePickerState = { ...this.datePickerState, [dateToChange]: newDate };
        }
    };

    public render(): JSX.Element {
        const {
            config: { heading, className, showChannelInfo, backOrderOnlyCheckbox, showDatePickerFilters, searchByPoCheckbox },
            resources: {
                genericErrorMessage,
                noOrderHistoryLable,
                moreButtonText,
                backToShopping,
                orderCountLabel,
                ordersCountLabel,
                loadingLabel,
                orderHistorySearchFieldPlaceholder,
                orderHistorySearchFieldPONumberPlaceholder,
                invalidStartDate,
                invalidEndDate
            }
        } = this.props;
        const { salesOrders, products } = this.orderHistory;
        const { hasError, isShowMoreButton, isLoading } = this.viewState;
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const channelIdentities = this.props.data.channels.result && this.props.data.channels.result.channelIdentities;
        const orgUnitLocations = this.props.data.orgUnitLocations.result && this.props.data.orgUnitLocations.result.orgUnitLocations;
        let viewProps: IOrderHistoryViewProps = {
            ...this.props,
            orderHistory: this.orderHistory,
            viewState: this.viewState,
            orderHistoryProps: {
                moduleProps: this.props,
                className: classname('ms-order-history', className)
            },
            header: {
                headerProps: { className: 'ms-order-history__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        {...heading}
                        className='ms-order-history__heading'
                        text={heading.text}
                        tag={heading.tag || 'h2'}
                        editProps={{ onEdit: this.handleOrderHistoryHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-history__btn-keep-shopping' href={backToShoppingUrl}>
                    {backToShopping}
                </Button>
            ),
            orderHistorySearchFieldPlaceholder: this.props.resources.orderHistorySearchFieldPlaceholder,
            getHistory: (searchFilter: string) => this.getOrderHistory(false, searchFilter),
            loading: <Label className='ms-order-history__loading' label={loadingLabel} />,
            backOrderOnlyCheckbox: backOrderOnlyCheckbox || false,
            backOrderOnlyToggle: this._renderBackOrderOnlyToggle(
                this.backOrderToggleState.isBackOrderOnlyChecked,
                this._handleBackOrderOnlyChange
            ),
            searchByPoCheckbox: searchByPoCheckbox || true,
            searchByPoToggle: this._renderSearchByPoToggle(this.searchByPoToggleState.isSearchByPoChecked, this._handleSearchByPoChange),
            datePickers: this._renderDatePickers(invalidStartDate, invalidEndDate),
            showDatePickers: showDatePickerFilters || false,
            searchPlaceholderText: this.searchByPoToggleState.isSearchByPoChecked
                ? orderHistorySearchFieldPONumberPlaceholder
                : orderHistorySearchFieldPlaceholder
        };

        if (hasError) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-history__alert' label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!salesOrders || salesOrders.length === 0) {
            viewProps = {
                ...viewProps,
                emptyMessage: <p className='ms-order-history__empty-message'>{noOrderHistoryLable}</p>
            };

            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const _orderCountLabel = salesOrders.length === 1 ? orderCountLabel : ordersCountLabel;
        this.payLoad.contentAction.etext = TelemetryConstant.ViewMore;
        const viewMoreAttributes = getTelemetryAttributes(this.telemetryContent!, this.payLoad);

        const productsDetailedViewLimit = 5;

        viewProps = {
            ...viewProps,
            header: {
                ...viewProps.header,
                orderCountLabel: <span className='ms-order-history__order-count'>{`(${salesOrders.length} ${_orderCountLabel})`}</span>,
                extraActions: this.renderExtraActions()
            },
            table:
                this._viewMode === OrderHistoryViewMode.listView ? (
                    <OrderHistoryTableComponent
                        id={this.props.id}
                        typeName={this.props.typeName}
                        context={this.props.context}
                        className='ms-order-history__table-list'
                        shouldShowChannelInfo={this.props.config.showChannelInfo ?? false}
                        data={{
                            orderHistory: this.orderHistory,
                            channelIdentities,
                            orgUnitLocations
                        }}
                        resources={this.props.resources}
                    />
                ) : (
                    undefined
                ),
            list:
                this._viewMode === OrderHistoryViewMode.detailedView
                    ? {
                          listProps: { className: 'ms-order-history__sales-order-list' },
                          salesOrders: salesOrders.map(salesOrder =>
                              getSalesOrder({
                                  ...this.props,
                                  products,
                                  salesOrder,
                                  channelIdentities,
                                  orgUnitLocations,
                                  showChannelInfo,
                                  salesLinesLimit: productsDetailedViewLimit,
                                  telemetryContent: this.telemetryContent
                              })
                          )
                      }
                    : undefined,
            moreButton: isShowMoreButton && (
                <Button
                    className={`ms-order-history__btn-more ${isLoading ? 'is-busy' : ''}`}
                    {...viewMoreAttributes}
                    disabled={isLoading}
                    onClick={this.onMoreButtonClick}
                >
                    {moreButtonText}
                </Button>
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderBackOrderOnlyToggle = (
        includeBackOrderOnly: boolean,
        handleBackOrderOnlyChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void
    ): React.ReactNode => (
        <div className='ms-order-history__back-order-container'>
            <label className='ms-order-history__back-order-container-label'>
                <span className={'ms-order-history__back-order-container-label__heading'}>Display Back Order Only</span>
                <input
                    value={includeBackOrderOnly ? 1 : 0}
                    onClick={handleBackOrderOnlyChange}
                    type='checkbox'
                    className='ms-order-history__back-order-container-label__checkbox'
                />
                <div
                    className={`ms-order-history__back-order-container-label__toggle ms-order-history__back-order-container-label__toggle--${
                        includeBackOrderOnly ? 'checked' : 'unchecked'
                    }`}
                />
            </label>
        </div>
    );
    private _renderSearchByPoToggle = (
        searchByPo: boolean,
        handleSearchByPoChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void
    ): React.ReactNode => (
        <div className='ms-order-history__back-order-container'>
            <label className='ms-order-history__back-order-container-label'>
                <span className={'ms-order-history__back-order-container-label__heading'}>Search By PO</span>
                <input
                    value={searchByPo ? 1 : 0}
                    onClick={handleSearchByPoChange}
                    type='checkbox'
                    className='ms-order-history__back-order-container-label__checkbox'
                />
                <div
                    className={`ms-order-history__back-order-container-label__toggle ms-order-history__back-order-container-label__toggle--${
                        searchByPo ? 'checked' : 'unchecked'
                    }`}
                />
            </label>
        </div>
    );

    private _renderDatePickers = (startDateError: string, endDateError: string): React.ReactNode => (
        <div className='eve-order-history-search-container__date-picker-wrapper'>
            <DatePickerFilter
                startDate={this.datePickerState.startDate}
                endDate={this.datePickerState.endDate}
                handleDateChange={this._handleDateChange}
                fromLabel={this.props.resources.fromDatePickerLabel}
                toLabel={this.props.resources.toDatePickerLabel}
            />
            <p className='datepicker-error'>
                {this.viewState.invalidStartDate ? startDateError : ''}
                {this.viewState.invalidEndDate ? endDateError : ''}
            </p>
        </div>
    );

    private _handleBackOrderOnlyChange = () => {
        const newBackOrderToggleState = {
            isBackOrderOnlyChecked: !this.backOrderToggleState.isBackOrderOnlyChecked
        };
        this.backOrderToggleState = newBackOrderToggleState;
    };

    private _handleSearchByPoChange = () => {
        const newSearchByPoToggleState = {
            isSearchByPoChecked: !this.searchByPoToggleState.isSearchByPoChecked
        };
        this.searchByPoToggleState = newSearchByPoToggleState;
    };

    public handleOrderHistoryHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);

    private readonly onMoreButtonClick = () => {
        this._updateOrderHistory(true);
    };

    // Removed the rendering of the Order History Filter to disable to the ability to select search by Organization-Wide
    private readonly renderExtraActions = () => {
        return (
            <>
                {this._renderOrderHistoryFilter()}
                {this._renderViewModes()}
            </>
        );
    };

    /**
     * Renders a filter for order history.
     * @returns A react node or undefined if customer info is not ready.
     */
    private readonly _renderOrderHistoryFilter = () => {
        return (
            this.props.data.customerInformation &&
            this.props.data.customerInformation.result && (
                <OrderHistoryFilter
                    id={this.props.id}
                    typeName={this.props.typeName}
                    context={this.props.context}
                    className='ms-order-history__ms-order-history-filter'
                    resources={this.props.resources}
                    data={{ customerInformation: this.props.data.customerInformation.result }}
                    currentState={this.filterState}
                    onFilterStateChanged={this.onFilterStateChanged}
                />
            )
        );
    };

    /**
     * Renders view mode switches for order history.
     * @returns A react node.
     */
    private readonly _renderViewModes = () => {
        if (!this.props.config.isGridViewEnabled) {
            return null;
        }

        const viewModesClassName = 'ms-order-history__ms-order-history-view-modes';
        return (
            <div className={viewModesClassName}>
                <Button
                    className={classname(
                        `${viewModesClassName}__detailed-view`,
                        this._viewMode === OrderHistoryViewMode.detailedView ? 'msc-view-modes__selected' : ''
                    )}
                    title={this.props.resources.orderHistoryDetailedViewModeAriaLabel}
                    aria-label={this.props.resources.orderHistoryDetailedViewModeAriaLabel}
                    role='button'
                    onClick={this._setDetailedViewMode}
                />
                <Button
                    className={classname(
                        `${viewModesClassName}__list-view`,
                        this._viewMode === OrderHistoryViewMode.listView ? 'msc-view-modes__selected' : ''
                    )}
                    title={this.props.resources.orderHistoryListViewModeAriaLabel}
                    aria-label={this.props.resources.orderHistoryListViewModeAriaLabel}
                    role='button'
                    onClick={this._setListViewMode}
                />
            </div>
        );
    };

    private readonly initState = () => {
        this.orderHistory = {
            salesOrders: [],
            products: [],
            nextPageToken: ''
        };
        this.viewState = {
            isDataReady: false,
            hasError: false,
            isShowMoreButton: true,
            isLoading: false,
            invalidStartDate: false,
            invalidEndDate: false
        };
        this.pageSize = this.props.config.pageSize || 2;
        this.paging = {
            top: this.pageSize,
            skip: 0
        };
    };

    /**
     * Updates view mode to be list view.
     */
    private readonly _setListViewMode = () => {
        this._viewMode = OrderHistoryViewMode.listView;
    };

    /**
     * Updates view mode to be detailed view.
     */
    private readonly _setDetailedViewMode = () => {
        this._viewMode = OrderHistoryViewMode.detailedView;
    };

    private readonly onFilterStateChanged = (state: OrderHistoryFilterState) => {
        this.initState();
        this.filterState = state;
        this._updateOrderHistory();
    };

    /**
     * Retrieves the data and updates the view state.
     */
    private readonly _updateOrderHistory = (isAppend: boolean = false) => {
        this.viewState.isDataReady = false;
        this.getOrderHistory(isAppend)
            .then(() => {
                this.viewState.isDataReady = true;
            })
            .catch(() => {
                this.viewState.hasError = true;
            });
    };

    private readonly getOrderHistory = async (isAppend: boolean = false, searchFilter?: string): Promise<void> => {
        if (!isAppend) {
            //If we're not appending, we are searching for new orders - reset skip
            this.paging.skip = 0;
        }
        const actionContext = this.props.context.actionContext;
        const input = new GetSalesOrderHistoryWithHydrationsInput(
            this.paging,
            this.props.context.request.apiSettings,
            this.backOrderToggleState.isBackOrderOnlyChecked,
            this.searchByPoToggleState.isSearchByPoChecked,
            this.datePickerState,
            this.filterState,
            this.orderHistory.nextPageToken,
            searchFilter
        );
        try {
            this.viewState.isLoading = true;
            this.viewState.hasError = false;
            const response = await getSalesOrderHistoryWithHydrationsAction(input, actionContext);
            if (response) {
                const { products, salesOrders } = response;
                if (isAppend) {
                    this.orderHistory.products.push(...products);
                    this.orderHistory.salesOrders.push(...salesOrders);
                } else {
                    this.orderHistory.products = products;
                    this.orderHistory.salesOrders = salesOrders;
                }
                this.viewState.isShowMoreButton =
                    this.viewState.isShowMoreButton && response.salesOrders.length > 0 && response.salesOrders.length >= this.pageSize;
                this.paging.skip += this.pageSize;
                this.orderHistory.nextPageToken = response.nextPageToken;
            }
            this.viewState.isLoading = false;
            await Promise.resolve();
            return;
        } catch (err) {
            console.error(err);
            this.viewState.isLoading = false;
            this.viewState.hasError = true;
            return Promise.reject();
        }
    };
}

export default OrderHistory;
